import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useTheme } from "../contexts/ThemeContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Eye, EyeOff, Shield, Moon, Sun } from "lucide-react";
import { formatError } from "../lib/api";

export default function AdminLogin() {
  const { login, user } = useAuth();
  const { isDark, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const [email, setEmail] = useState("admin@wonstepcareer.com");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    window.history.replaceState(null, "", "/");
    window.history.pushState(null, "", "/admin/login");
  }, []);

  useEffect(() => {
    if (user?.role === "admin") navigate("/admin");
  }, [user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const u = await login(email, password);
      if (u.role === "admin") {
        navigate("/admin");
      } else {
        setError("These credentials do not have admin access.");
      }
    } catch (err) {
      setError(formatError(err));
    } finally {
      setLoading(false);
    }
  };

  const pageBg = isDark ? '#0a0a0f' : '#f8f9fa';
  const cardBg = isDark ? 'rgba(255,255,255,0.05)' : '#ffffff';
  const cardBorder = isDark ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(0,0,0,0.08)';
  const cardShadow = isDark ? 'none' : '0 4px 24px rgba(0,0,0,0.06)';
  const textPrimary = isDark ? '#ffffff' : '#0a0a0f';
  const textSecondary = isDark ? 'rgba(255,255,255,0.5)' : '#64748b';
  const textMuted = isDark ? 'rgba(255,255,255,0.3)' : '#94a3b8';
  const inputStyle = isDark
    ? { background: 'rgba(255,255,255,0.07)', borderColor: 'rgba(255,255,255,0.12)', color: 'white' }
    : { background: 'rgba(0,0,0,0.04)', borderColor: 'rgba(0,0,0,0.1)', color: '#0a0a0f' };
  const accent = '#059669';
  const accentDark = '#047857';
  const glowColor = isDark ? 'rgba(5,150,105,0.12)' : 'rgba(249,115,22,0.06)';

  return (
    <div className="min-h-screen flex items-center justify-center px-4 relative" style={{ background: pageBg }}>
      {/* Subtle ambient glow */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-96 h-96 rounded-full blur-3xl" style={{ background: `radial-gradient(circle, ${glowColor}, transparent)` }} />
      </div>

      {/* Theme toggle */}
      <button
        onClick={toggleTheme}
        aria-label="Toggle theme"
        className="absolute top-4 right-4"
        style={{ width: '32px', height: '32px', borderRadius: '50%', background: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: textSecondary }}
      >
        {isDark ? <Sun size={14} /> : <Moon size={14} />}
      </button>

      <div className="w-full max-w-sm relative">
        {/* Logo */}
        <div className="text-center mb-6">
          <Link to="/" className="inline-flex items-center gap-2" style={{ textDecoration: 'none' }}>
            <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: `linear-gradient(135deg, ${accent}, ${accentDark})` }}>
              <span className="text-white font-bold text-sm">W</span>
            </div>
            <span className="text-xl font-bold" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", color: textPrimary }}>WonStepCareer</span>
          </Link>
        </div>

        <Card className="shadow-2xl" style={{ background: cardBg, backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)', border: cardBorder, boxShadow: cardShadow }}>
          <CardHeader>
            <div className="w-10 h-10 rounded-xl flex items-center justify-center mb-3" style={{ background: isDark ? 'rgba(5,150,105,0.15)' : 'rgba(5,150,105,0.1)' }}>
              <Shield size={20} style={{ color: accent }} />
            </div>
            <CardTitle className="text-xl" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", color: textPrimary }}>Admin Portal</CardTitle>
            <CardDescription style={{ color: textSecondary }}>Restricted access — administrators only</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="email" className="text-sm" style={{ color: textSecondary }}>Admin Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  required
                  className="mt-1 placeholder:text-gray-400"
                  style={inputStyle}
                  data-testid="admin-email"
                />
              </div>
              <div>
                <Label htmlFor="password" className="text-sm" style={{ color: textSecondary }}>Password</Label>
                <div className="relative mt-1">
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    placeholder="Admin password"
                    required
                    className="pr-10 placeholder:text-gray-400"
                    style={inputStyle}
                    data-testid="admin-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2"
                    style={{ color: textMuted }}
                  >
                    {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>

              {error && (
                <p className="text-sm text-red-500 p-3 rounded-lg" style={{ background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)' }} data-testid="admin-login-error">
                  {error}
                </p>
              )}

              <Button
                type="submit"
                disabled={loading}
                className="w-full font-semibold"
                style={{ background: `linear-gradient(135deg, ${accent}, ${accentDark})`, color: 'white', boxShadow: `0 2px 12px rgba(5,150,105,0.3)` }}
                data-testid="admin-login-btn"
              >
                {loading ? "Signing in..." : "Sign In to Admin Panel"}
              </Button>
            </form>
          </CardContent>
        </Card>

        <p className="text-center mt-4 text-sm" style={{ color: textMuted }}>
          Recruiter?{" "}
          <Link to="/recruiter/login" className="font-medium" style={{ color: textSecondary }}>Recruiter Login</Link>
        </p>
      </div>
    </div>
  );
}
